// frontend/src/components/ErrorBoundary.js
//
// Nothing caught a render-time throw. React unmounts the whole tree when a
// render throws and, with no boundary anywhere, that left a white page with no
// text, no navigation and no way back — indistinguishable from the app being
// dead. One bad date from the API (date-fns throws on an invalid one) was
// enough to do it to a customer standing at a locker.
//
// i18next is used directly rather than through the hook, because a class
// component cannot use hooks and because this must still render if the tree
// that provides context is the very thing that just fell over.
import React from 'react';
import i18n from '../i18n';
import theme from '../theme';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    // The stack is the only trace of this once the screen has been replaced.
    console.error('Unhandled render error:', error, info?.componentStack);
  }

  render() {
    if (!this.state.failed) return this.props.children;

    // A full reload, not a state reset: whatever data shape caused the throw is
    // still in memory, so re-rendering the same tree would throw again.
    const reload = () => { window.location.href = '/'; };

    return (
      <div style={{
        minHeight: '100vh', background: theme.cream, display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 24,
        fontFamily: theme.font.body, textAlign: 'center',
      }}>
        <div style={{ maxWidth: 420 }}>
          <h1 style={{ fontFamily: theme.font.display, fontSize: 32, color: theme.ink, fontWeight: 400, marginBottom: 12 }}>
            {i18n.t('errorBoundary.title')}
          </h1>
          <p style={{ color: theme.grey550, fontSize: 14, lineHeight: 1.7, marginBottom: 28 }}>
            {i18n.t('errorBoundary.body')}
          </p>
          <button onClick={reload} style={{
            background: theme.ink, color: theme.white, border: 'none', padding: '14px 32px',
            borderRadius: 2, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase',
            cursor: 'pointer', fontFamily: 'inherit',
          }}>
            {i18n.t('errorBoundary.reload')}
          </button>
          <p style={{ color: theme.grey400, fontSize: 12, lineHeight: 1.7, marginTop: 24 }}>
            {i18n.t('errorBoundary.contact')}{' '}
            <a href="mailto:support@glamgo.hair" style={{ color: theme.gold }}>support@glamgo.hair</a>
          </p>
        </div>
      </div>
    );
  }
}
